import { useState, useCallback } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useToast } from '@/components/ToastContainer'
import { apiFetch } from '@/lib/api'
import {
  Select,
  Input,
  Button,
  Table,
  Pagination,
  Alert,
  type TableColumn,
} from '@/ui'
import { StatusBadge } from '@/components/StatusBadge'
import { formatDate } from '@/ui'
import styles from './ActivationCodes.module.css'

interface ActivationCode {
  id: string
  code: string
  type: string
  credits: number
  status: string
  usedBy: string | null
  usedByEmail: string | null
  usedAt: string | null
  smartstoreOrderId: string | null
  createdAt: string
  expiresAt: string | null
}

interface CodesResponse {
  codes: ActivationCode[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean }
}

const TYPE_LABELS: Record<string, string> = {
  PREMIUM_UPGRADE: '프리미엄',
  AI_CREDITS: 'AI 크레딧',
  AI_CREDITS_BUNDLE: '크레딧 번들',
  INVITATION_MAIN: '청첩장(슬롯3)',
  INVITATION_PACKAGE: '청첩장(슬롯3)',
  THANKYOU_ADDON: '감사장 추가',
  THANKS_PACKAGE: '감사장 패키지',
  ALL_PACKAGE: '올인원 패키지',
}

// 코드 생성 폼 type 옵션 (크레딧형 + 활성화형). dev 테스트·운영 보상용.
const CREATE_TYPE_OPTIONS = [
  { value: 'PREMIUM_UPGRADE', label: '프리미엄' },
  { value: 'AI_CREDITS', label: 'AI 크레딧' },
  { value: 'AI_CREDITS_BUNDLE', label: '크레딧 번들' },
  { value: 'INVITATION_PACKAGE', label: '청첩장(활성화 슬롯3)' },
  { value: 'THANKYOU_ADDON', label: '감사장 추가(+2)' },
  { value: 'THANKS_PACKAGE', label: '감사장 패키지' },
  { value: 'ALL_PACKAGE', label: '올인원 패키지' },
]

const STATUS_LABELS: Record<string, string> = {
  AVAILABLE: '사용 가능',
  USED: '사용됨',
  EXPIRED: '만료',
}

export function ActivationCodes() {
  const { addToast } = useToast()

  // 필터
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  // 생성 폼
  const [createType, setCreateType] = useState<string>('AI_CREDITS')
  const [createCredits, setCreateCredits] = useState(10)
  const [createCount, setCreateCount] = useState(1)
  const [creating, setCreating] = useState(false)

  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('pageSize', '20')
  if (statusFilter) params.set('status', statusFilter)
  if (typeFilter) params.set('type', typeFilter)

  const { data, isLoading, error, refetch } = useApiFetch<CodesResponse>(
    `/api/admin/activation-codes?${params.toString()}`,
  )

  const handleCreate = useCallback(async () => {
    if (creating) return
    setCreating(true)
    try {
      // admin의 apiFetch는 성공 시 data를 언랩해 반환하고 실패 시 throw 한다
      const data = await apiFetch<{ codes: { code: string }[] }>(
        '/api/admin/activation-codes',
        {
          method: 'POST',
          body: JSON.stringify({ type: createType, credits: createCredits, count: createCount }),
        },
      )
      const codes = data.codes.map((c) => c.code).join(', ')
      addToast({ type: 'success', message: `${data.codes.length}개 코드 생성: ${codes}` })
      refetch()
    } catch {
      addToast({ type: 'error', message: '코드 생성 실패' })
    } finally {
      setCreating(false)
    }
  }, [creating, createType, createCredits, createCount, addToast, refetch])

  const handleCopyCode = useCallback((code: string) => {
    navigator.clipboard.writeText(code)
    addToast({ type: 'success', message: `${code} 복사됨` })
  }, [addToast])

  const columns: TableColumn<ActivationCode>[] = [
    {
      key: 'code',
      label: '코드',
      width: '160px',
      render: (row) => (
        <span
          className={styles.codeCell}
          onClick={() => handleCopyCode(row.code)}
          title="클릭하여 복사"
        >
          {row.code}
        </span>
      ),
    },
    { key: 'type', label: '타입', render: (row) => TYPE_LABELS[row.type] ?? row.type, width: '100px' },
    { key: 'credits', label: '크레딧', render: (row) => `${row.credits}개`, width: '70px' },
    { key: 'status', label: '상태', render: (row) => <StatusBadge status={row.status} label={STATUS_LABELS[row.status] ?? row.status} />, width: '90px' },
    { key: 'usedByEmail', label: '사용자', render: (row) => row.usedByEmail ?? '-', width: '180px' },
    { key: 'smartstoreOrderId', label: '주문ID', render: (row) => row.smartstoreOrderId ?? '-', width: '120px' },
    { key: 'createdAt', label: '생성일', render: (row) => formatDate(row.createdAt, 'yyyy.M.d HH:mm'), width: '130px' },
    { key: 'usedAt', label: '사용일', render: (row) => row.usedAt ? formatDate(row.usedAt, 'yyyy.M.d HH:mm') : '-', width: '130px' },
  ]

  return (
    <div className={styles.root}>
      {/* 코드 생성 */}
      <div className={styles.createSection}>
        <Select
          size="sm"
          value={createType}
          onChange={(e) => setCreateType(e.target.value)}
          options={CREATE_TYPE_OPTIONS}
        />
        <Input
          size="sm"
          type="number"
          min={0}
          max={1000}
          value={createCredits}
          onChange={(e) => setCreateCredits(Math.min(1000, Math.max(0, Number(e.target.value))))}
          style={{ width: '100px' }}
          placeholder="크레딧 수"
        />
        <Input
          size="sm"
          type="number"
          min={1}
          max={50}
          value={createCount}
          onChange={(e) => setCreateCount(Math.min(50, Math.max(1, Number(e.target.value))))}
          style={{ width: '80px' }}
          placeholder="코드 수"
        />
        <Button size="sm" onClick={handleCreate} disabled={creating}>
          {creating ? '생성중...' : '코드 생성'}
        </Button>
      </div>

      {/* 필터 */}
      <div className={styles.filters}>
        <Select
          size="sm"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
          options={[
            { value: '', label: '전체 상태' },
            { value: 'AVAILABLE', label: '사용 가능' },
            { value: 'USED', label: '사용됨' },
            { value: 'EXPIRED', label: '만료' },
          ]}
        />
        <Select
          size="sm"
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
          options={[{ value: '', label: '전체 타입' }, ...CREATE_TYPE_OPTIONS]}
        />
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<ActivationCode>
        columns={columns}
        data={data?.codes ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={setPage}
          size="sm"
        />
      )}
    </div>
  )
}
